import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { testimonials } from '../../mocks/testimonials';
import QuoteModal from '../../components/feature/QuoteModal';
import SchemaOrgHead from '../../components/feature/SchemaOrgHead';

const filterConfig = [
  { value: 'Todos', labelKey: 'sf_all' },
  { value: 'Limpieza Post-Mudanza', labelKey: 'sf_move_out' },
  { value: 'Limpieza de Ventanas', labelKey: 'sf_windows' },
  { value: 'Lavado a Presión', labelKey: 'sf_pressure' },
  { value: 'Valet Trash - Condominio', labelKey: 'sf_valet' },
];

const guarantees = [
  {
    icon: 'ri-thumb-up-line',
    color: 'bg-brand-green-light/40',
    iconColor: 'text-brand-green-dark',
    tKey: 'guar_t1',
    dKey: 'guar_d1',
  },
  {
    icon: 'ri-user-follow-line',
    color: 'bg-brand-blue-light/30',
    iconColor: 'text-brand-blue-dark',
    tKey: 'guar_t2',
    dKey: 'guar_d2',
  },
  {
    icon: 'ri-leaf-line',
    color: 'bg-brand-green-light/40',
    iconColor: 'text-brand-green-dark',
    tKey: 'guar_t3',
    dKey: 'guar_d3',
  },
  {
    icon: 'ri-time-line',
    color: 'bg-brand-blue-light/30',
    iconColor: 'text-brand-blue-dark',
    tKey: 'guar_t4',
    dKey: 'guar_d4',
  },
];

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

const opinionesSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/opiniones`,
  url: `${siteUrl}/opiniones`,
  name: 'Opiniones de Clientes | Limpieza Pro The Villages FL',
  description:
    'Opiniones y compromiso de calidad de Limpieza Pro The Villages FL. Servicios de limpieza residencial, post-mudanza, ventanas y lavado a presión en The Villages, FL con satisfacción 100% garantizada.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Opiniones', item: `${siteUrl}/opiniones` },
    ],
  },
  mainEntity: {
    '@type': 'LocalBusiness',
    name: 'Limpieza Pro The Villages FL',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'The Villages',
      addressRegion: 'FL',
      addressCountry: 'US',
    },
  },
};

export default function OpinionesPage() {
  const [filter, setFilter] = useState('Todos');
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { t } = useTranslation();

  const filtered =
    filter === 'Todos' ? testimonials : testimonials.filter((item) => item.service === filter);

  const hasReviews = testimonials.length > 0;

  return (
    <>
      <SchemaOrgHead
        schema={opinionesSchema}
        pageTitle="Opiniones de Clientes | Limpieza Residencial The Villages, FL — Limpieza Pro"
        pageDescription="Limpieza Pro The Villages FL ofrece limpieza residencial con satisfacción 100% garantizada. Sé de los primeros en probar nuestro servicio en The Villages, FL."
        canonicalPath="/opiniones"
      />

      {/* Hero */}
      <section className="relative pt-36 pb-20 overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/12fc3f761103f02a631b2a8da413ff38.jpg"
          alt="Hogar limpio y brillante"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-blue-dark/85 to-brand-green-dark/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block bg-white/10 border border-white/20 text-brand-green-light text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wider uppercase">
            {t('revp_badge')}
          </span>
          <h1 className="font-heading font-bold text-white text-5xl mb-5">{t('revp_title')}</h1>
          <p className="text-white/75 text-base max-w-xl mx-auto leading-relaxed">
            {t('revp_desc')}
          </p>
        </div>
      </section>

      {/* Our Promise — always visible */}
      <section className="py-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-brand-green font-semibold text-sm uppercase tracking-wider">
              {t('rev_eyebrow')}
            </span>
            <h2 className="font-heading font-bold text-brand-blue-dark text-3xl mt-2 mb-3">
              {t('rev_heading')}
            </h2>
            <div className="w-12 h-1 bg-brand-green rounded-full mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {guarantees.map((g) => (
              <div key={g.tKey} className="rounded-2xl p-6 flex items-start gap-4 bg-gray-50">
                <div
                  className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${g.color}`}
                >
                  <i className={`${g.icon} text-xl ${g.iconColor}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-brand-blue-dark text-sm mb-1">{t(g.tKey)}</h3>
                  <p className="text-gray-400 text-xs leading-relaxed">{t(g.dKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter — always visible, ready for real reviews */}
      <section className="py-6 bg-gray-50 border-b border-gray-100 sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-6 overflow-x-auto">
          <div className="flex gap-2 min-w-max">
            {filterConfig.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  filter === f.value
                    ? 'bg-brand-green text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-brand-green hover:text-brand-green'
                }`}
              >
                {t(f.labelKey)}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Grid or Empty State */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          {hasReviews && filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((item) => (
                <div key={item.id} className="bg-white rounded-2xl p-6 flex flex-col">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={`ri-star-${i < item.rating ? 'fill' : 'line'} text-yellow-400 text-sm`}
                        />
                      ))}
                    </div>
                    <span className="bg-brand-green-light/50 text-brand-green-dark text-xs font-semibold px-3 py-1 rounded-full">
                      {item.service}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed flex-1 italic mb-5">
                    &ldquo;{item.text}&rdquo;
                  </p>
                  <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-10 h-10 rounded-full object-cover object-top"
                    />
                    <div>
                      <p className="font-semibold text-brand-blue-dark text-sm">{item.name}</p>
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <i className="ri-map-pin-line text-xs" /> {item.location}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ─── Beautiful empty state ─── */
            <div className="max-w-2xl mx-auto text-center py-16">
              <div className="w-24 h-24 flex items-center justify-center rounded-full bg-white border-2 border-dashed border-brand-green/30 mx-auto mb-6">
                <i className="ri-chat-smile-3-line text-4xl text-brand-green/50" />
              </div>
              <h3 className="font-heading font-bold text-brand-blue-dark text-2xl mb-3">
                {t('revp_empty_h')}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-md mx-auto">
                {t('revp_empty_d')}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                {[
                  { icon: 'ri-shield-check-line', label: '100% Satisfacción' },
                  { icon: 'ri-award-line', label: 'Servicio Garantizado' },
                  { icon: 'ri-time-line', label: 'Respuesta en 24h' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="bg-white rounded-xl p-4 flex flex-col items-center gap-2"
                  >
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-brand-green-light/50">
                      <i className={`${item.icon} text-brand-green-dark text-lg`} />
                    </div>
                    <p className="text-brand-blue-dark text-xs font-semibold">{item.label}</p>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setQuoteOpen(true)}
                className="inline-flex items-center gap-2 bg-brand-green hover:bg-brand-green-dark text-white font-bold px-8 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line" />
                {t('guar_first_btn')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-white border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-brand-green-light/50 mx-auto mb-5">
            <i className="ri-star-line text-brand-green-dark text-3xl" />
          </div>
          <h2 className="font-heading font-bold text-brand-blue-dark text-3xl mb-4">
            {t('revp_cta_t')}
          </h2>
          <p className="text-gray-500 text-sm mb-8 max-w-md mx-auto">{t('revp_cta_d')}</p>
          <button
            onClick={() => setQuoteOpen(true)}
            className="bg-gradient-to-r from-brand-green-dark to-brand-blue text-white font-bold px-10 py-4 rounded-full hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            {t('revp_cta_btn')}
          </button>
        </div>
      </section>

      <QuoteModal isOpen={quoteOpen} onClose={() => setQuoteOpen(false)} />
    </>
  );
}
